import { MailOutline } from '@mui/icons-material';
import { Box, Button, CloseIcon, IconButton, Typography } from 'drift-ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLimitedFreightAccess } from '../features/findLoads/hooks/userAccessControlBanner';
import { CompanyPermissions } from '../features/findLoads/permissionsType';
import { setShowHighwayRegistrationFlow } from '../state/showHighwayRegistrationFlowSlice';
import {
  CurrentUser,
  getHasBookItNowPermissions,
  hasFullAccessPermissions,
} from '../utilities/authUtils';
import Icon, { ImageName } from './Icon';
import { openPermissionDialog } from '../features/findLoads/permissionsState';
import { RootState } from '../configureStore';

interface PermissionsBannerProps {
  currentUser?: CurrentUser;
  companyPermissions?: CompanyPermissions;
}

type BannerKind =
  | 'upgradeRequested'
  | 'upgradeAvailable'
  | 'highwaySignup'
  | 'limitedAccess'
  | null;

const bannerStyles = (useUpgradeRequestedStyles: boolean) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: useUpgradeRequestedStyles ? 'none' : 'blue.100',
  padding: '4px 24px 0px 32px',
  height: '100px',
  minWidth: '1084px',
  borderBottom: '1px solid gray.300',
  boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.22)',
  zIndex: 100,
});

const bannerContentStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const PermissionsBanner: React.FC<PermissionsBannerProps> = ({
  currentUser,
  companyPermissions,
}) => {
  const { config } = useSelector((state: RootState) => state.permissionDialog);
  const { t } = useTranslation();
  const { hasLimitedAccess, contactHref, needsHighwaySignup } =
    useLimitedFreightAccess();
  const [showBanner, setShowBanner] = useState(true);
  const dispatch = useDispatch();
  console.log('PermissionsBanner render', {
    hasLimitedAccess,
    needsHighwaySignup,
    showBanner,
  });
  const upgradeRequested =
    currentUser?.metaData?.ryanTransportationOnboarding
      ?.loadboardAccessRequested || config.permissionRequested;

  const userHasLimitedAccess =
    currentUser?.mcleod_carrier_id && hasLimitedAccess;

  const userCanRequestBINAccess =
    companyPermissions &&
    hasFullAccessPermissions(companyPermissions.companyPermissions) &&
    !getHasBookItNowPermissions();

  let bannerKind: BannerKind = null;

  if (!userHasLimitedAccess && userCanRequestBINAccess) {
    bannerKind = upgradeRequested ? 'upgradeRequested' : 'upgradeAvailable';
  } else if (needsHighwaySignup || !currentUser?.mcleod_carrier_id) {
    bannerKind = 'highwaySignup';
  } else if (userHasLimitedAccess) {
    bannerKind = 'limitedAccess';
  }

  if (!bannerKind || !showBanner) {
    return null;
  }

  const bannerConfig: Record<
    Exclude<BannerKind, null>,
    {
      title: string;
      text: string;
      buttonText: string;
      icon: ImageName;
      buttonVariant: 'contained' | 'outlined';
      startIcon?: React.ReactNode;
      useUpgradeRequestedStyles: boolean;
    }
  > = {
    upgradeRequested: {
      title: 'resubmit_upgrade_request_title',
      text: 'resubmit_upgrade_request_body',
      buttonText: 'resubmit_upgrade_request_cta',
      icon: 'Glass',
      buttonVariant: 'outlined',
      useUpgradeRequestedStyles: true,
    },
    upgradeAvailable: {
      title: 'request_upgrade_title',
      text: 'request_upgrade_body',
      buttonText: 'request_upgrade_cta',
      icon: 'EmailCircle',
      buttonVariant: 'contained',
      useUpgradeRequestedStyles: false,
    },
    highwaySignup: {
      title: 'highway_signup_title',
      text: 'highway_signup_body',
      buttonText: 'highway_access_cta',
      icon: 'FreightBox',
      buttonVariant: 'contained',
      useUpgradeRequestedStyles: false,
    },
    limitedAccess: {
      title: 'limited_access_title',
      text: 'limited_access_body',
      buttonText: 'limited_access_cta',
      icon: 'FreightAlert',
      buttonVariant: 'contained',
      startIcon: <MailOutline />,
      useUpgradeRequestedStyles: false,
    },
  };

  const currentBanner = bannerConfig[bannerKind];

  const handleButtonOnClick = () => {
    if (
      bannerKind === 'upgradeRequested' ||
      bannerKind === 'upgradeAvailable'
    ) {
      dispatch(
        openPermissionDialog({
          formType: 'upgrade',
          hideBackdrop: false,
        })
      );
      return;
    }

    if (bannerKind === 'highwaySignup') {
      dispatch(setShowHighwayRegistrationFlow(true));
      return;
    }

    globalThis.location.href = `${contactHref}`;
  };

  const handleOnClosedBanner = () => {
    localStorage.setItem('permissionsBannerDismissed', 'true');
    setShowBanner(false);
  };

  return (
    <Box sx={bannerStyles(currentBanner.useUpgradeRequestedStyles)}>
      <Box sx={{ ...bannerContentStyles, gap: '20px' }}>
        <Icon
          name={currentBanner.icon}
          alt="Freight Alert"
          height={100}
          width={100}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" fontWeight={600} color="gray.900">
            {t(currentBanner.title)}
          </Typography>
          <Typography variant="body1" color="gray.900">
            {t(currentBanner.text)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ ...bannerContentStyles, gap: '24px' }}>
        <Button
          variant={currentBanner.buttonVariant}
          color="primary"
          startIcon={currentBanner.startIcon}
          onClick={handleButtonOnClick}
        >
          {t(currentBanner.buttonText)}
        </Button>
        <IconButton
          size="medium"
          onClick={handleOnClosedBanner}
          aria-label="Close Permissions Banner"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PermissionsBanner;
